import { Component } from '@angular/core';
import {
  AnalyticsService,
  CustomerService,
  EnvironmentService,
  LanguageService,
  TranslationService,
} from '@proximus/ng-common';
import { OnDestroyHookDirective } from '../../../shared/hooks';
import {
  Alert,
  AlertsService,
  ModalService,
  PxModalMessageService,
} from '@proximus/ng-common-components';
import {
  getMprdProductType,
  hasWebdeal,
  InternetProduct,
  isAsOrAsasOption,
  isDisneyPlusActive,
  isDisneyPlusNotActive,
  isNetflixOption,
  isOptionActive,
  isResCustomer,
  MobileProduct,
  NetflixBuyAndSellStatus,
  Product,
  TvProduct,
} from '@dch/products-business-logic';
import { Location } from '@angular/common';
import { getAlertKey, onResult } from 'src/app/shared/order-utils/order-utils';
import { DisneyPlusModalComponent } from '../disney-plus-modal/disney-plus-modal.component';
import { buildTaggingProduct } from '../../../tagging/tagging.utils';
import { DISNEY_PLUS_MPUID_CODES } from '../../constants';



@Component({
  selector: 'mprd-option-details-base',
  template: '',
})
export class OptionDetailsBaseComponent extends OnDestroyHookDirective {
  deepLinkMpcUuid: string;
  selectedProduct: MobileProduct | InternetProduct | TvProduct | Product;
  option: any;
  hasWebdeal: boolean;
  webdealSuffix: string;
  optionName: string;
  optionDescriptionName: string;
  treatmentCode: string;
  isRes: boolean;
  internalSsid: string;
  pendingContextTagging: string;
  productType: string;

  // tagging
  isWebshopLoaded: boolean;
  isWebshopPending: boolean;
  shouldReportCardImpression = true;

  isAsOrAsasOption = isAsOrAsasOption;
  isNetflixOption = isNetflixOption;
  isDisneyPlusActive = isDisneyPlusActive;
  isDisneyPlusNotActive = isDisneyPlusNotActive;

  isSmoothAccess = this.environmentService.isSmoothAccess;

  constructor(
    public translationService: TranslationService,
    public languageService: LanguageService,
    public alertService: AlertsService,
    public customerService: CustomerService,
    public location: Location,
    private modalService: ModalService,
    private pxModalMessageService: PxModalMessageService,
    public analyticsService: AnalyticsService,
    protected environmentService: EnvironmentService,
  ) {
    super();
  }

  onResult(event) {
    onResult(
      event,
      this.deepLinkMpcUuid,
      this.selectedProduct,
      this.option,
      this.translationService,
      this.languageService,
      this.modalService,
      this.pxModalMessageService,
      this.alertService,
      this.treatmentCode,
      getMprdProductType(this.selectedProduct) === 'tv',
      false,
    );
  }

  onWebshopLoadedEvent(event) {
    this.isWebshopLoaded = true;
    this.isWebshopPending = event.pendingContextTagging;
    this.reportCardImpressionTaggingEvent();
  }

  public reportCardImpressionTaggingEvent() {
    if (
      this.shouldReportCardImpression &&
      this.isWebshopLoaded &&
      this.selectedProduct &&
      this.option
    ) {
      this.shouldReportCardImpression = false;
      this.analyticsService.reportEvent({
        event: 'card-impression',
        card: {
          zone: this.productType + ' options ordering',
          name: this.optionName,
        },
        product: buildTaggingProduct(this.selectedProduct, this.option),
        context: this.isWebshopPending
          ? this.translationService.getTranslatedValueFor(
            'message.warning.pending.title',
          )
          : undefined,
      });
    }
  }

  protected dispatchAlert(
    type: 'error' | 'info' | 'success' | 'warning',
    title: string,
    description: string,
  ): void {
    const alert: Alert = {
      isClosable: true,
      alertKey: this.getAlertKey(),
      title,
      description,
      type,
      useNegativeColors:
        this.selectedProduct &&
        getMprdProductType(this.selectedProduct) === 'tv',
    };
    this.alertService.dispatchAlert(alert);
  }

  public getDownloadAppstoreImageByOs(os: string): string {
    switch (os.toUpperCase()) {
      case 'ANDROID':
        return this.translationService.getTranslatedValueFor(
          'mobile.downloadLink.image.android',
        );
      case 'IOS':
        return this.translationService.getTranslatedValueFor(
          'mobile.downloadLink.image.ios',
        );
      case 'WINDOWS':
        return this.translationService.getTranslatedValueFor(
          'mobile.downloadLink.image.windows',
        );
      default:
        return '';
    }
  }

  protected getWebdealSuffix() {
    if (!this.environmentService.isSmoothAccess) {
      this.addSubscription(
        this.customerService.getCustomerAccount().subscribe((value) => {
          this.isRes = isResCustomer(value);
          this.webdealSuffix =
            value.getSelectedCustomer().subsegment === 'RES' ? 'res' : 'se';
          this.webdealSuffix +=
            '-' + this.languageService.getSelectedLanguage();
        }),
      );
    }
  }

  protected hasWebdeals() {
    this.hasWebdeal = this.option && hasWebdeal(this.option);
  }

  public openDisneyPlusModal() {
    this.modalService
      .create<DisneyPlusModalComponent>(
        DisneyPlusModalComponent,
        undefined,
        undefined,
        undefined,
        true,
      )
      .subscribe((componentRef) => { });
  }

  public isActiveOption() {
    return this.option && isOptionActive(this.option);
  }

  public setOptionName() {
    if (this.option) {
      if (
        this.option.packDescription &&
        this.isNetflixBuyAndSellAvailableOrActive()
      ) {
        this.optionName = this.option.packDescription;
      } else {
        this.optionName = this.option.name;
      }
    }
  }

  public setOptionDescriptionName() {
    if (this.option && this.option.usp) {
      this.optionDescriptionName =
        this.option.usp[this.languageService.getSelectedLanguage()];
    }
  }

  public goBack() {
    this.location.back();
  }

  private getAlertKey(): string {
    return getAlertKey(this.deepLinkMpcUuid, this.option);
  }

  public isNetflixBuyAndSellAvailableOrActive() {
    return (
      [
        NetflixBuyAndSellStatus.AVAILABLE,
        NetflixBuyAndSellStatus.ACTIVE,
      ].indexOf((this.selectedProduct as TvProduct).netflixBuyAndSell) > -1
    );
  }

  public isDisneyPlusOption(option: any) {
    return (
      DISNEY_PLUS_MPUID_CODES.includes(option?.optionCodeForDescription) ||
      this.isAsOrAsasOption(option) //If Disney+ also included in other packages
    );
  }
}

